import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Typography,
  DateCTA,
  NewDateFIlterModal as DateFilterModal,
  Loader,
  MultiSelect,
} from "components";
import { getRegions } from "redux/actions/regionActions";
import { capitalizeFirstLetter } from "utils/helper";
import {
  getRegAgentsStats,
  setRegStatusDates,
  setRegStatusRegion,
} from "redux/actions/dashboard/onboardingDashboardActions";
import { useWindowDimensions } from "hooks";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

const AgentsRegistrationStatus = () => {
  const [openDateModal, setOpenDateModal] = useState(false);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const { regions } = useSelector((state) => state.regions);

  const { width } = useWindowDimensions();

  const isSmallScreen = width < 992;

  useEffect(() => {
    getRegions();
  }, []);

  const { userType, regStatus } = useSelector(
    (state) => state.onboardingReducer
  );

  const handleModalClose = () => setOpenDateModal(false);

  const typeOfUser = userType.toLowerCase();

  const { data, loading, dates, regionCode } = regStatus || {};

  const getAgentsData = () => {
    getRegAgentsStats(userType, dates, regionCode);
    handleModalClose();
  };

  useEffect(() => {
    getAgentsData();
    //eslint-disable-next-line
  }, [userType]);

  const {
    isError,
    enabled,
    deactivated,
    registered,
    ongoingRegistration,
    awaitingAuthorization,
    rejectedRegistration,
  } = data || {};

  const agentsData = [
    {
      label: "Enabled",
      value: enabled,
      color: "#102E88",
    },
    {
      label: "Disabled",
      value: deactivated,
      color: "#778CA2",
    },

    {
      label: "Ongoing Registration",
      value: ongoingRegistration,
      color: "#1966FC",
    },
    {
      label: "Awaiting Authorization",
      value: awaitingAuthorization,
      color: "#3784FB",
    },
    {
      label: "Rejected Registration",
      value: rejectedRegistration,
      color: "#E4EAFD",
    },
  ];

  const displayChart = () => registered > 0 && !isError;

  const getRegionOptions = () =>
    regions?.map((region) => ({
      label: region.name,
      value: region.regionCode,
    }));

  const handleRegionsChange = (val) => {
    setSelectedRegions(val);
    val.length
      ? setRegStatusRegion(val.map((i) => i.value))
      : setRegStatusRegion(null);
  };

  return (
    <div className="dashboard-card">
      <div className="row pt-4 px-4">
        <div className="col-7 text-center">
          <Typography
            className="text-center mt-1 "
            state="primary"
            variant="body0"
          >
            {capitalizeFirstLetter(typeOfUser)}s Per Registration Status
          </Typography>
          <div
            style={{
              margin: "1.2rem 0 .5rem 0rem",
            }}
          >
            <MultiSelect
              placeholder={"All Regions"}
              value={selectedRegions}
              disabled={loading}
              isSearchable={false}
              clearable={false}
              handleChange={handleRegionsChange}
              isLoading={regions.loading}
              options={getRegionOptions()}
              onBlur={() => getAgentsData()}
              name="region-select"
            />
          </div>
          <div className="d-flex justify-content-center flex-wrap">
            {selectedRegions.length === regions.length ||
            !selectedRegions.length ? (
              <Typography variant="caption0">All Regions</Typography>
            ) : (
              selectedRegions.map((region, i) => (
                <Typography variant="caption0" key={i}>
                  {region.label}
                  {i === selectedRegions.length - 1 ? "." : ","} &nbsp;
                </Typography>
              ))
            )}
          </div>

          {loading ? (
            <div
              className={"text-center"}
              style={{ marginTop: "6rem", height: "13rem" }}
            >
              <Loader />
              <Typography state="grey" variant="body2">
                Loading data...
              </Typography>
            </div>
          ) : displayChart() ? (
            <div
              style={{
                position: "relative",
                height: "18rem",
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart width={500} height={500}>
                  <Pie
                    data={agentsData.map((entry) => ({ value: entry.value }))}
                    dataKey="value"
                    cx="50%"
                    cy="50%"
                    innerRadius={isSmallScreen ? 55 : 75}
                    outerRadius={isSmallScreen ? 80 : 110}
                    fill="#82ca9d"
                    stroke=""
                  >
                    {agentsData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={agentsData.map((entry) => entry.color)[index]}
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              {registered && (
                <div className={`${typeOfUser}-total-agents  text-center`}>
                  <Typography variant="heading4" state="primary">
                    {registered}
                  </Typography>
                  <Typography
                    variant="body2"
                    state="primary"
                    style={{ marginTop: "-.5rem" }}
                  >
                    {capitalizeFirstLetter(typeOfUser)}
                    {registered > 1 ? "s" : ""}
                  </Typography>
                </div>
              )}
            </div>
          ) : (
            <div style={{ height: "15rem" }}>
              <Typography
                state="grey"
                variant="body2"
                className={" text-center mtop-4 mbottom-4"}
              >
                No information to display
              </Typography>
            </div>
          )}
        </div>
        <div className="col-5 pt-1">
          <div className="d-flex justify-content-end">
            <DateCTA onClick={() => setOpenDateModal(true)} />
          </div>
          {!loading && displayChart() && (
            <div style={{ marginTop: "4rem" }}>
              {agentsData.map((entry, index) => (
                <div className="mt-3 d-flex align-items-center" key={index}>
                  <div
                    className="indicator-box"
                    style={{ backgroundColor: entry.color }}
                  ></div>
                  <div>
                    <Typography state="primary" variant="caption1">
                      {entry.label}
                    </Typography>
                    <Typography variant="body4">
                      {entry.value} {capitalizeFirstLetter(typeOfUser)}
                      {entry.value > 1 ? "s" : ""}
                    </Typography>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <DateFilterModal
        dateFrom={dates.dateFrom}
        dateTo={dates.dateTo}
        openDateModal={openDateModal}
        onClose={handleModalClose}
        handleFromChange={(val) => setRegStatusDates(val, "dateFrom")}
        handleToChange={(val) => setRegStatusDates(val, "dateTo")}
        onButtonClick={getAgentsData}
      />
    </div>
  );
};

export default AgentsRegistrationStatus;
